import { useEffect, useState } from "react";
import axios from "../axios";
import DashboardStats from "../components/DashboardStats";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const WA_SERIES = [
  { key: "total_leads", label: "Total Leads", color: "#2563eb", dash: "0" },
  { key: "new_leads", label: "New Leads", color: "#64748b", dash: "5 3" },
  { key: "interested", label: "Interested", color: "#16a34a", dash: "0" },
  { key: "followups", label: "Follow-ups", color: "#7c3aed", dash: "4 2" },
  { key: "booked", label: "Booked", color: "#ea580c", dash: "0" }
];

const SOCIAL_SERIES = [
  { key: "total_leads", label: "Total Leads", color: "#2563eb", dash: "0" },
  { key: "dms", label: "DMs", color: "#16a34a", dash: "0" },
  { key: "comments", label: "Comments", color: "#db2777", dash: "5 3" },
  { key: "booked", label: "Booked", color: "#ea580c", dash: "0" },
  { key: "followups", label: "Follow-ups", color: "#7c3aed", dash: "4 2" }
];

function Chip({ label, value, accent, bg, text }) {
  return (
    <div
      style={{
        background: bg,
        borderRadius: 12,
        padding: "14px 16px",
        borderTop: `3px solid ${accent}`,
        boxShadow: "0 1px 4px rgba(0,0,0,.07)"
      }}
    >
      <p style={{ fontSize: 11, color: "#6b7280", margin: "0 0 6px" }}>{label}</p>
      <p style={{ fontSize: 22, fontWeight: 700, color: text, margin: 0 }}>
        {(value || 0).toLocaleString()}
      </p>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, maxWidth: 200 }}>
      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
          <span style={{ color: "#374151", fontSize: 12 }}>{p.name}</span>
          <b style={{ color: p.color, fontSize: 12 }}>{p.value}</b>
        </div>
      ))}
    </div>
  );
}

function ChartLegend({ series }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginBottom: 14 }}>
      {series.map(({ label, color, dash }) => (
        <span key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
          <svg width="18" height="10" viewBox="0 0 18 10">
            <line x1="0" y1="5" x2="18" y2="5" stroke={color} strokeWidth="2.5" strokeDasharray={dash} />
          </svg>
          {label}
        </span>
      ))}
    </div>
  );
}

const CustomDot = ({ cx, cy, stroke }) => (
  <circle cx={cx} cy={cy} r={4} fill="#fff" stroke={stroke} strokeWidth={2.5} />
);

function StatCard({ title, value, color = "text-gray-900" }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow">
      <h3 className="text-gray-500 text-sm">{title}</h3>
      <p className={`text-2xl font-bold mt-2 ${color}`}>{value || 0}</p>
    </div>
  );
}

export default function Dashboard() {
  const [wallet, setWallet] = useState(0);
  const [socialStats, setSocialStats] = useState({});
  const [dateRange, setDateRange] = useState("all");
  const [analysisType, setAnalysisType] = useState("whatsapp");
  const [waRange, setWaRange] = useState("7");
  const [waAnalytics, setWaAnalytics] = useState([]);
  const [socialAnalytics, setSocialAnalytics] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [stats, setStats] = useState({ contacts: 0, campaigns: 0, sent: 0 });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get("/auth/me");
      setWallet(res.data.wallet || 0);
      const statsRes = await axios.get("/campaigns/dashboard-stats");
      setStats({
        contacts: statsRes.data.contacts || 0,
        campaigns: statsRes.data.campaigns || 0,
        sent: statsRes.data.sent || 0
      });
    } catch (err) {
      console.log("Dashboard error:", err.response?.data || err.message);
    }
  };

  const loadSocialStats = async () => {
    try {
      const res = await axios.get(`/dashboard/social-stats?range=${dateRange}`);
      setSocialStats(res.data || {});
    } catch (err) {
      console.error("Social stats error:", err.response?.data || err.message);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      if (analysisType === "whatsapp") {
        const res = await axios.get(`/dashboard/whatsapp-analytics?range=${waRange}`);
        setWaAnalytics(res.data || []);
      } else {
        const res = await axios.get(`/dashboard/social-analytics?platform=${analysisType}&range=${waRange}`);
        setSocialAnalytics(res.data || []);
      }
    } catch (err) {
      console.error("Analytics error:", err.response?.data || err.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchData();
      await loadSocialStats();
      await loadAnalytics();
    };
    loadData();
    const timer = setInterval(loadData, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [dateRange, waRange, analysisType]);

  const waTotals = waAnalytics.reduce((acc, item) => {
    WA_SERIES.forEach(({ key }) => { acc[key] = (acc[key] || 0) + Number(item[key] || 0); });
    return acc;
  }, {});

  const socialTotals = socialAnalytics.reduce((acc, item) => {
    SOCIAL_SERIES.forEach(({ key }) => { acc[key] = (acc[key] || 0) + Number(item[key] || 0); });
    return acc;
  }, {});

  const chartData =
    analysisType === "whatsapp"
      ? waAnalytics.map((item) => ({
          date: item.date ? String(item.date).slice(5, 10) : "",
          total_leads: Number(item.total_leads || 0),
          new_leads: Number(item.new_leads || 0),
          interested: Number(item.interested || 0),
          followups: Number(item.followups || 0),
          booked: Number(item.booked || 0)
        }))
      : socialAnalytics.map((item) => ({
          date: item.date ? String(item.date).slice(5, 10) : "",
          total_leads: Number(item.total_leads || 0),
          dms: Number(item.dms || 0),
          comments: Number(item.comments || 0),
          booked: Number(item.booked || 0),
          followups: Number(item.followups || 0)
        }));

  const activeSeries = analysisType === "whatsapp" ? WA_SERIES : SOCIAL_SERIES;
  const activeTotals = analysisType === "whatsapp" ? waTotals : socialTotals;

  const activeChips =
    analysisType === "whatsapp"
      ? [
          { key: "total_leads", label: "Total Leads", accent: "#2563eb", bg: "#eff6ff", text: "#1d4ed8" },
          { key: "new_leads", label: "New Leads", accent: "#64748b", bg: "#f1f5f9", text: "#334155" },
          { key: "interested", label: "Interested", accent: "#16a34a", bg: "#f0fdf4", text: "#15803d" },
          { key: "followups", label: "Follow-ups", accent: "#7c3aed", bg: "#f5f3ff", text: "#6d28d9" },
          { key: "booked", label: "Booked", accent: "#ea580c", bg: "#fff7ed", text: "#c2410c" }
        ]
      : [
          { key: "total_leads", label: "Total Leads", accent: "#2563eb", bg: "#eff6ff", text: "#1d4ed8" },
          { key: "dms", label: "DMs", accent: "#16a34a", bg: "#f0fdf4", text: "#15803d" },
          { key: "comments", label: "Comments", accent: "#db2777", bg: "#fdf2f8", text: "#be185d" },
          { key: "booked", label: "Booked", accent: "#ea580c", bg: "#fff7ed", text: "#c2410c" },
          { key: "followups", label: "Follow-ups", accent: "#7c3aed", bg: "#f5f3ff", text: "#6d28d9" }
        ];

  const analysisTitle =
    analysisType === "whatsapp"
      ? "WhatsApp Leads Analysis"
      : analysisType === "instagram"
      ? "Instagram Leads Analysis"
      : "Facebook Leads Analysis";

  const totalSocialMessages =
    Number(socialStats.instagram_messages || 0) +
    Number(socialStats.facebook_messages || 0);

  const socialMessageTitle =
    dateRange === "today" ? "Today Social Messages"
    : dateRange === "week" ? "Last 7 Days Messages"
    : dateRange === "month" ? "Last 30 Days Messages"
    : "Total Social Messages";

  // Responsive padding: less on mobile
  const pagePadding = isMobile ? "16px" : "24px";

  // Chip grid: auto-fit so the row always wraps to fit the real available
  // width (instead of a hard column count tied to window.innerWidth, which
  // doesn't account for a sidebar shrinking the actual content area).
  const chipGridCols = "repeat(auto-fit, minmax(130px, 1fr))";

  return (
    <div style={{ padding: pagePadding }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: isMobile ? 16 : 24,
        // on mobile leave space for hamburger button (56px)
        paddingLeft: isMobile ? 52 : 0
      }}>
        <h1 style={{ fontSize: isMobile ? 22 : 30, fontWeight: 700, margin: 0 }}>Dashboard</h1>
      </div>

      {/* ── Leads Analysis Card ── */}
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #f1f5f9",
          boxShadow: "0 2px 12px rgba(0,0,0,.07)",
          padding: isMobile ? "16px" : "24px 28px",
          marginBottom: 24
        }}
      >
        {/* Card header */}
        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: 16,
          gap: 12
        }}>
          <div>
            <h2 style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, margin: 0 }}>{analysisTitle}</h2>
            <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>Real-time lead funnel tracking</p>
          </div>

          {/* Controls */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
            {/* Platform buttons */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["whatsapp", "instagram", "facebook"].map((type) => (
                <button
                  key={type}
                  onClick={() => setAnalysisType(type)}
                  style={{
                    padding: isMobile ? "6px 10px" : "8px 13px",
                    borderRadius: 8,
                    border: analysisType === type ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: analysisType === type ? "#eff6ff" : "#fff",
                    color: analysisType === type ? "#1d4ed8" : "#374151",
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    textTransform: "capitalize"
                  }}
                >
                  {type}
                </button>
              ))}
            </div>

            {/* Range select */}
            <select
              value={waRange}
              onChange={(e) => setWaRange(e.target.value)}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: isMobile ? "6px 10px" : "8px 14px",
                fontSize: isMobile ? 12 : 13,
                width: isMobile ? "100%" : "auto"
              }}
            >
              <option value="1">Today</option>
              <option value="7">Last 7 Days</option>
              <option value="15">Last 15 Days</option>
              <option value="30">Last 30 Days</option>
            </select>
          </div>
        </div>

        {/* Chips grid */}
        <div style={{ display: "grid", gridTemplateColumns: chipGridCols, gap: 10, marginBottom: 16 }}>
          {activeChips.map(({ key, label, accent, bg, text }) => (
            <Chip key={key} label={label} value={activeTotals[key]} accent={accent} bg={bg} text={text} />
          ))}
        </div>

        {/* Chart */}
        <div style={{ background: "#fafafa", borderRadius: 12, border: "1px solid #f1f5f9", padding: isMobile ? 10 : 16 }}>
          <ChartLegend series={activeSeries} />
          <div style={{ height: isMobile ? 220 : 300 }}>
            {chartData.length === 0 ? (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 13, textAlign: "center" }}>
                No {analysisType} analytics data found for selected range.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: isMobile ? -20 : -10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: isMobile ? 9 : 11, fill: "#9ca3af" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: isMobile ? 9 : 11, fill: "#9ca3af" }} />
                  <Tooltip content={<CustomTooltip />} />
                  {activeSeries.map(({ key, label, color, dash }) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={label}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray={dash === "0" ? undefined : dash}
                      dot={isMobile ? false : <CustomDot stroke={color} />}
                      activeDot={{ r: 5, fill: color, stroke: "#fff", strokeWidth: 2 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div style={{ marginBottom: 20 }}>
        <DashboardStats />
      </div>

      {/* Messages / Contacts / Campaigns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <StatCard title="Messages Left" value={wallet} color="text-green-600" />
        <StatCard title="Total Contacts" value={stats.contacts} color="text-blue-600" />
        <StatCard title="Campaigns" value={stats.campaigns} color="text-purple-600" />
      </div>

      {/* Meta Social header */}
      <div style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "center",
        gap: 10,
        marginBottom: 16
      }}>
        <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, margin: 0 }}>Meta Social Performance</h2>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: "8px 14px",
            background: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,.06)",
            fontSize: 13,
            width: isMobile ? "100%" : "auto"
          }}
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
        </select>
      </div>

      {/* Social top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard title="Social Leads" value={socialStats.total_social_leads} color="text-indigo-600" />
        <StatCard title="Social DMs" value={socialStats.total_dms} color="text-blue-600" />
        <StatCard title="Social Comments" value={socialStats.total_comments} color="text-pink-600" />
        <StatCard
          title={socialMessageTitle}
          value={dateRange === "all" ? socialStats.today_social_messages : totalSocialMessages}
          color="text-orange-600"
        />
      </div>

      {/* Social platform stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard title="Instagram Messages" value={socialStats.instagram_messages} color="text-pink-600" />
        <StatCard title="Facebook Messages" value={socialStats.facebook_messages} color="text-blue-600" />
        <StatCard title="Social Bookings" value={socialStats.booked_leads} color="text-green-600" />
        <StatCard title="Social Follow-ups" value={socialStats.followups} color="text-purple-600" />
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Leads With Phone" value={socialStats.leads_with_phone} color="text-emerald-600" />
        <StatCard title="Total Social Messages" value={totalSocialMessages} color="text-gray-900" />
        <div className="bg-white p-5 rounded-xl shadow">
          <h3 className="text-gray-500 text-sm">Meta Social Status</h3>
          <p className="text-lg font-bold text-green-600 mt-2">Active ✅</p>
          <p className="text-sm text-gray-500 mt-1">Instagram + Facebook inbox connected</p>
        </div>
      </div>
    </div>
  );
}